
import React, { useRef, useState } from 'react';
import { EstateAssets, LifetimeGift } from '../../../types';
import { Gift, Plus, Trash2, Calendar, Upload, Download, AlertCircle, CheckCircle, XCircle, FileText } from 'lucide-react';
import { HelpPopover } from '../../HelpPopover';
import { GiftImportModal } from './GiftImportModal';
import { ParsedGiftRow, downloadGiftTemplate, parseGiftCsv, parseGiftXlsx } from './giftImportUtils';

interface Props {
    data: EstateAssets;
    onChange: (data: EstateAssets) => void;
    onNext?: () => void;
}

export const GiftsStep: React.FC<Props> = ({ data, onChange }) => {
    const gifts = data.lifetime_gifts || [];
    const hasGifts = gifts.length > 0;

    const [gatewayAnswer, setGatewayAnswer] = useState<boolean | null>(hasGifts ? true : null);
    const [importRows, setImportRows] = useState<ParsedGiftRow[] | null>(null);
    const [importError, setImportError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const updateGifts = (newGifts: LifetimeGift[]) => {
        onChange({ ...data, lifetime_gifts: newGifts });
    };

    const addGift = () => {
        const newGift: LifetimeGift = {
            id: Math.random().toString(36).substr(2, 9),
            date: '',
            amount: 0,
            recipient_type: 'other',
            is_regular_from_income: false,
            donor_attribution: 'deceased',
        };
        updateGifts([...gifts, newGift]);
    };

    const updateGift = (id: string, field: keyof LifetimeGift, value: any) => {
        updateGifts(gifts.map(g => g.id === id ? { ...g, [field]: value } : g));
    };

    const removeGift = (id: string) => {
        updateGifts(gifts.filter(g => g.id !== id));
    };

    // ---------------------------------------------------------------------------
    // Import handling
    // ---------------------------------------------------------------------------

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!e.target) return;
        // Reset so same file can be re-selected
        (e.target as HTMLInputElement).value = '';
        if (!file) return;

        setImportError(null);

        try {
            let rows: ParsedGiftRow[];
            if (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')) {
                const buffer = await file.arrayBuffer();
                rows = await parseGiftXlsx(buffer);
            } else {
                const text = await file.text();
                rows = parseGiftCsv(text);
            }

            if (rows.length === 0) {
                setImportError('No data rows found. Make sure your file has a header row and at least one data row.');
                return;
            }
            setImportRows(rows);
        } catch (err: any) {
            setImportError(err?.message || 'Failed to read file');
        }
    };

    const handleImportConfirm = (newGifts: LifetimeGift[]) => {
        // Drop empty placeholder gifts so imports start at Gift 1
        const existing = gifts.filter(g => g.date || g.amount > 0 || g.description);
        updateGifts([...existing, ...newGifts]);
        setImportRows(null);
        setGatewayAnswer(true);
    };

    return (
        <div className="max-w-2xl mx-auto space-y-8 animate-in slide-in-from-right fade-in duration-500">
            <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-accent-soft text-accent mb-4">
                    <Gift className="w-6 h-6" />
                </div>
                <h2 className="text-2xl font-bold text-ink">Lifetime Gifts</h2>
                <p className="text-ink3 mt-2">
                    Gifts made in the 7 years before death can affect Inheritance Tax.
                </p>
            </div>

            {/* Gateway Question */}
            <div className="bg-white p-6 rounded-xl border border-rule">
                <h3 className="font-semibold text-ink mb-2">
                    Did the deceased give away money or assets in the 7 years before they died?
                </h3>
                <p className="text-xs text-ink3 mb-4">
                    Include cash, property, or valuable items. Exclude small birthday/Christmas gifts (under £250).
                </p>

                <div className="flex gap-4">
                    <button
                        onClick={() => { setGatewayAnswer(true); if (gifts.length === 0) addGift(); }}
                        className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all
                            ${gatewayAnswer === true
                                ? 'border-accent bg-accent-soft text-accent'
                                : 'border-rule hover:border-accent text-ink3'}
                        `}
                    >
                        Yes, they made gifts
                    </button>
                    <button
                        onClick={() => { setGatewayAnswer(false); updateGifts([]); }}
                        className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all
                            ${gatewayAnswer === false
                                ? 'border-ink bg-paper text-ink'
                                : 'border-rule hover:border-rule text-ink3'}
                        `}
                    >
                        No gifts
                    </button>
                </div>
            </div>

            {/* Income Gift Exemption Screening — surfaced only after at least
                one gift has been entered, so the question has concrete context.
                Per-gift "regular from income" checkboxes (see further down) are
                the canonical capture; this block is the s21 explainer. */}
            {gatewayAnswer === true && gifts.length > 0 && (
                <div className="bg-white p-6 rounded-xl border border-rule">
                    <h3 className="font-semibold text-ink mb-2 flex items-center gap-2">
                        Were any of these gifts part of a regular pattern made from the deceased's income?
                        <HelpPopover content={{
                            what_it_means: "Gifts made regularly out of surplus income (not capital) may be fully exempt from IHT under the 'normal expenditure out of income' exemption (IHTA84 s21).",
                            examples: ["Monthly payments to family members", "Regular standing orders", "School fee payments", "Recurring financial support"]
                        }} />
                    </h3>
                    <p className="text-xs text-ink3 mb-2">
                        Examples include:
                    </p>
                    <ul className="text-xs text-ink3 mb-3 list-disc list-inside space-y-0.5">
                        <li>monthly payments to family members</li>
                        <li>regular standing orders</li>
                        <li>school fee payments</li>
                        <li>recurring financial support</li>
                    </ul>
                    <p className="text-xs text-ink3 mb-4">
                        To qualify for the "normal expenditure out of income" exemption, the gifts must:
                    </p>
                    <ul className="text-xs text-ink3 mb-4 list-disc list-inside space-y-0.5">
                        <li>have been made from income (not savings or capital)</li>
                        <li>form part of a regular pattern of giving</li>
                        <li>not reduce the deceased's usual standard of living</li>
                    </ul>

                    <div className="flex gap-3">
                        {([
                            { value: true, label: 'Yes' },
                            { value: false, label: 'No' },
                            { value: null, label: 'Unsure' },
                        ] as const).map(opt => (
                            <button
                                key={String(opt.value)}
                                onClick={() => onChange({ ...data, possible_income_gift_exemption: opt.value })}
                                className={`flex-1 py-2.5 px-3 rounded-lg border-2 text-sm font-medium transition-all
                                    ${data.possible_income_gift_exemption === opt.value
                                        ? 'border-accent bg-accent-soft text-accent'
                                        : 'border-rule hover:border-accent text-ink3'}
                                `}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>

                    {(data.possible_income_gift_exemption === true || data.possible_income_gift_exemption === null) && data.possible_income_gift_exemption !== false && data.possible_income_gift_exemption !== undefined && (
                        <div className="mt-3 bg-accent-soft border border-accent rounded-lg p-3 text-xs text-accent">
                            <strong>Note:</strong> Gifts that qualify for the "normal expenditure out of income" exemption may be fully exempt from inheritance tax and will not use the Nil Rate Band. You can mark individual gifts as "regular from income" below.
                        </div>
                    )}
                </div>
            )}

            {/* Gift List */}
            {gatewayAnswer === true && (
                <div className="space-y-4">

                    {/* Import toolbar */}
                    <div className="flex items-center justify-between">
                        <p className="text-xs text-ink3">
                            {gifts.length} gift{gifts.length !== 1 ? 's' : ''} added
                        </p>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={downloadGiftTemplate}
                                className="flex items-center gap-1.5 text-xs text-ink3 border border-rule rounded-lg px-3 py-1.5 hover:bg-paper transition-colors"
                            >
                                <Download className="w-3.5 h-3.5" />
                                Download template
                            </button>
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="flex items-center gap-1.5 text-xs font-bold text-accent border border-accent bg-accent-soft rounded-lg px-3 py-1.5 hover:bg-accent-soft transition-colors"
                            >
                                <Upload className="w-3.5 h-3.5" />
                                Upload spreadsheet
                            </button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                className="hidden"
                                onChange={handleFileSelect}
                            />
                        </div>
                    </div>

                    {/* Upload error */}
                    {importError && (
                        <div className="flex items-start gap-2 bg-paper border border-rule rounded-lg p-3 text-xs text-ink2">
                            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                            <div>
                                <p className="font-semibold mb-0.5">Upload failed</p>
                                <p>{importError}</p>
                            </div>
                        </div>
                    )}

                    {/* Itemisation guidance — combining recipients in one row
                          misapplies the per-recipient £250 small-gift exemption
                          and the annual-exemption attribution. */}
                    {gifts.length > 0 && (
                        <p className="text-[11px] text-ink3 -mt-1">
                            Enter each gift to a different person as its own row and name the recipient. Combining several recipients in one line (e.g. “£250 each to several people”) can misapply the £250 small-gift exemption and the annual exemption.
                        </p>
                    )}

                    {/* Manual gift cards */}
                    {gifts.map((gift, index) => (
                        <div key={gift.id} className="bg-white p-5 rounded-xl border border-rule relative group">
                            <div className="absolute top-4 right-4">
                                <button
                                    onClick={() => removeGift(gift.id)}
                                    className="text-ink3 hover:text-ink2 transition-colors p-1"
                                    title="Remove gift"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>

                            <h4 className="font-bold text-sm text-ink2 mb-4 flex items-center gap-2">
                                <span className="bg-paper text-ink3 px-2 py-0.5 rounded text-xs">Gift {index + 1}</span>
                                {gift.description && (
                                    <span className="text-xs text-ink3 font-normal truncate max-w-[200px]">{gift.description}</span>
                                )}
                            </h4>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Date */}
                                <div>
                                    <label className="block text-xs font-bold text-ink3 mb-1">
                                        Date of Gift
                                    </label>
                                    <div className="relative">
                                        <Calendar className="absolute left-3 top-2.5 w-4 h-4 text-ink3" />
                                        <input
                                            type="month"
                                            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
                                            value={gift.date}
                                            onChange={(e) => updateGift(gift.id, 'date', e.target.value)}
                                        />
                                    </div>
                                </div>

                                {/* Amount */}
                                <div>
                                    <label className="block text-xs font-bold text-ink3 mb-1">
                                        Value (£)
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-2.5 text-ink3 font-bold">£</span>
                                        <input
                                            type="number"
                                            className="w-full pl-8 pr-3 py-2 border rounded-lg text-sm font-bold text-ink2"
                                            placeholder="0.00"
                                            value={gift.amount || ''}
                                            onChange={(e) => updateGift(gift.id, 'amount', parseFloat(e.target.value))}
                                        />
                                    </div>
                                </div>

                                {/* Recipient */}
                                <div className="md:col-span-2">
                                    <label className="block text-xs font-bold text-ink3 mb-1">
                                        Who received the gift?
                                    </label>
                                    <div className="grid grid-cols-3 gap-2">
                                        {[
                                            { id: 'spouse', label: 'Spouse / CP' },
                                            { id: 'charity', label: 'Charity' },
                                            { id: 'other', label: 'Other Person' }
                                        ].map(opt => (
                                            <button
                                                key={opt.id}
                                                onClick={() => updateGift(gift.id, 'recipient_type', opt.id)}
                                                className={`py-2 px-2 rounded border text-xs font-medium transition-all
                                                    ${gift.recipient_type === opt.id
                                                        ? 'bg-accent-soft border-accent text-accent'
                                                        : 'bg-white border-rule text-ink3 hover:border-rule'}
                                                `}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                    {gift.recipient_type === 'other' && (
                                        <input
                                            type="text"
                                            className="mt-2 w-full text-sm border border-rule rounded-lg p-2 focus:ring-2 focus:ring-accent/20"
                                            placeholder="Recipient's name (optional — groups the £250 small-gifts exemption per person)"
                                            value={gift.recipient_name ?? ''}
                                            onChange={(e) => updateGift(gift.id, 'recipient_name', e.target.value)}
                                        />
                                    )}
                                </div>

                                {/* Donor Attribution */}
                                <div className="md:col-span-2">
                                    <label className="block text-xs font-bold text-ink3 mb-1">
                                        Who made this gift?
                                    </label>
                                    <div className="grid grid-cols-4 gap-2">
                                        {([
                                            { id: 'deceased', label: 'Deceased only' },
                                            { id: 'spouse', label: 'Surviving spouse only' },
                                            { id: 'joint', label: 'Joint funds (50/50)' },
                                            { id: 'unsure', label: 'Unsure' },
                                        ] as const).map(opt => (
                                            <button
                                                key={opt.id}
                                                onClick={() => updateGift(gift.id, 'donor_attribution', opt.id)}
                                                className={`py-2 px-2 rounded border text-xs font-medium transition-all
                                                    ${(gift.donor_attribution ?? 'deceased') === opt.id
                                                        ? 'bg-accent-soft border-accent text-accent'
                                                        : 'bg-white border-rule text-ink3 hover:border-rule'}
                                                `}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                    {gift.donor_attribution === 'unsure' && (
                                        <p className="text-[10px] text-[#7A5A00] mt-1">
                                            Defaulting to 50% attributed to the deceased. Confirm the source of funds before filing.
                                        </p>
                                    )}
                                </div>

                                {/* Exemption Toggle - Regular Income */}
                                <div className="md:col-span-2 bg-paper p-3 rounded-lg flex items-center justify-between">
                                    <div className="flex-1 pr-4">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-ink">Regular gift from income</span>
                                            <HelpPopover content={{
                                                what_it_means: "Gifts made as part of normal expenditure (out of surplus income) are exempt if they don't reduce the giver's standard of living."
                                            }} />
                                        </div>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={gift.is_regular_from_income}
                                            onChange={(e) => updateGift(gift.id, 'is_regular_from_income', e.target.checked)}
                                        />
                                        <div className="w-11 h-6 bg-rule peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-rule after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent"></div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    ))}

                    <button
                        onClick={addGift}
                        className="w-full py-3 border-2 border-dashed border-rule rounded-xl text-ink3 hover:border-accent hover:text-accent hover:bg-accent-soft transition-all font-medium flex items-center justify-center gap-2"
                    >
                        <Plus className="w-4 h-4" /> Add another gift
                    </button>
                </div>
            )}

            {/* Income Assessment — year-by-year (IHT403 Schedule 2). Shown when
                  any gift is marked as regular. Each tax year that contains a
                  regular gift gets its own income/expenditure row; the s21
                  exemption is assessed against that year's surplus with no
                  carry-over between years. */}
            {gatewayAnswer === true && gifts.some(g => g.is_regular_from_income) && (() => {
                const ia = data.income_assessment ?? {};
                const existingYears = ia.years ?? [];

                const fmtGBP = (n: number) => n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });

                // Tax-year derivation must mirror the engine's getTaxYear exactly
                // (gift dates are 'YYYY-MM', parsed as the 1st of the month).
                const taxYearStart = (dateStr: string): number => {
                    const d = new Date(dateStr);
                    const y = d.getFullYear(), m = d.getMonth(), day = d.getDate();
                    return (m < 3 || (m === 3 && day < 6)) ? y - 1 : y;
                };
                const label = (start: number) => `${start}/${(start + 1).toString().slice(-2)}`;

                const regularGifts = gifts.filter(g => g.is_regular_from_income && g.date);
                const yearStarts = [...new Set(regularGifts.map(g => taxYearStart(g.date)))].sort((a, b) => a - b);
                const yearLabels = yearStarts.map(label);

                const rowFor = (yr: string) => existingYears.find(r => r.tax_year === yr) ?? { tax_year: yr };
                type YearField = 'pension_income' | 'employment_income' | 'rental_income' | 'investment_income' | 'other_income' | 'annual_living_expenses';
                const updateYear = (yr: string, field: YearField, value: number | null) => {
                    const others = existingYears.filter(r => r.tax_year !== yr);
                    const updated = { ...rowFor(yr), [field]: value };
                    const years = [...others, updated].sort((a, b) => (a.tax_year < b.tax_year ? -1 : 1));
                    onChange({ ...data, income_assessment: { ...ia, years } });
                };
                const setCarryOver = (val: boolean) => onChange({ ...data, income_assessment: { ...ia, allow_surplus_carry_over: val } });
                const incomeSourceFields: { key: YearField; label: string; placeholder: string }[] = [
                    { key: 'pension_income', label: 'Pension', placeholder: 'e.g. 18000' },
                    { key: 'employment_income', label: 'Employment', placeholder: 'e.g. 0' },
                    { key: 'rental_income', label: 'Rental', placeholder: 'e.g. 0' },
                    { key: 'investment_income', label: 'Investment', placeholder: 'e.g. 5000' },
                    { key: 'other_income', label: 'Other income', placeholder: 'e.g. 0' },
                    { key: 'annual_living_expenses', label: 'Living expenses', placeholder: 'e.g. 24000' },
                ];

                const yearRows = yearLabels.map(yr => {
                    const r = rowFor(yr);
                    const income = (r.pension_income ?? 0) + (r.employment_income ?? 0) + (r.rental_income ?? 0) + (r.investment_income ?? 0) + (r.other_income ?? 0);
                    const expenses = r.annual_living_expenses ?? 0;
                    const surplus = income - expenses;
                    const giftsTotal = regularGifts.filter(g => label(taxYearStart(g.date)) === yr).reduce((s, g) => s + (g.amount || 0), 0);
                    const exempt = Math.max(0, Math.min(giftsTotal, Math.max(0, surplus)));
                    const hasInput = income > 0 || expenses > 0;
                    return { yr, income, expenses, surplus, giftsTotal, exempt, hasInput, row: r };
                });

                const totalExempt = yearRows.reduce((s, y) => s + y.exempt, 0);
                const totalRegular = yearRows.reduce((s, y) => s + y.giftsTotal, 0);
                const anyInput = yearRows.some(y => y.hasInput);
                const singleYear = yearLabels.length < 2;

                return (
                    <div className="bg-white p-6 rounded-xl border border-accent space-y-6">
                        <div>
                            <h3 className="font-bold text-ink text-lg flex items-center gap-2">
                                <FileText className="w-5 h-5 text-accent" />
                                Regular Gifts & Income Assessment
                            </h3>
                            <p className="text-xs text-ink3 mt-1">
                                To assess the 'normal expenditure out of income' exemption (IHTA84 s21), enter approximate income and living expenses for each tax year in which regular gifts were made. The exemption is judged year by year — a gift is exempt only up to that year's surplus.
                            </p>
                        </div>

                        {/* Carry-over toggle */}
                        <div>
                            <label className="flex items-center gap-2 text-sm text-ink2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="rounded border-rule text-accent focus:ring-accent"
                                    checked={!!ia.allow_surplus_carry_over}
                                    onChange={(e) => setCarryOver(e.target.checked)}
                                />
                                Allow unused surplus to carry over between years
                            </label>
                            <p className="text-[10px] text-ink3 mt-1">
                                HMRC accepts accumulated surplus only for a limited period (broadly around two years) and it can be challenged. Leave off for the strict year-by-year test.
                            </p>
                        </div>

                        {/* Per-year cards — income by source + living expenses */}
                        <div className="space-y-4">
                            {yearRows.map(y => (
                                <div key={y.yr} className="border border-rule rounded-lg p-4 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <h4 className="text-sm font-bold text-ink2">Tax year {y.yr}</h4>
                                        <span className="text-xs text-ink3">Regular gifts: <span className="font-bold text-accent">{fmtGBP(y.giftsTotal)}</span></span>
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                        {incomeSourceFields.map(f => (
                                            <div key={f.key}>
                                                <label className="block text-[11px] font-medium text-ink3 mb-1">{f.label} (£)</label>
                                                <input
                                                    type="number"
                                                    className="w-full text-sm border border-rule rounded-lg p-2 focus:ring-2 focus:ring-accent/20"
                                                    placeholder={f.placeholder}
                                                    value={(y.row as any)[f.key] ?? ''}
                                                    onChange={(e) => updateYear(y.yr, f.key, e.target.value ? Number(e.target.value) : null)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    {y.hasInput && (
                                        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs border-t border-rule-soft pt-2">
                                            <span className="text-ink3">Surplus: <span className={`font-bold ${y.surplus < 0 ? 'text-[#7A5A00]' : 'text-ink2'}`}>{fmtGBP(y.surplus)}</span></span>
                                            <span className="text-ink3">Exempt this year: <span className="font-bold text-ink">{fmtGBP(y.exempt)}</span></span>
                                        </div>
                                    )}
                                </div>
                            ))}
                            <p className="text-[10px] text-ink3">Enter the deceased's income by source and normal living expenses for each year. Surplus = income − expenses.</p>
                        </div>

                        {/* Eligibility summary */}
                        {anyInput && (
                            <div className="border-t border-rule-soft pt-4 space-y-3">
                                {totalExempt >= totalRegular && totalExempt > 0 ? (
                                    <div className="flex items-start gap-2 bg-paper border border-rule rounded-lg p-3 text-sm text-ink">
                                        <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
                                        <div>
                                            <p className="font-bold">Regular gifts may qualify for the normal expenditure out of income exemption.</p>
                                            <p className="text-xs mt-1 text-ink2">Across the years entered, the regular gifts ({fmtGBP(totalRegular)}) are within the available surplus, so your estimate treats them as exempt. Executors must still formally claim this on form IHT403 (Schedule 2) with evidence of a regular pattern.</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-start gap-2 bg-[#FAF3E0] border border-[#E8D89A] rounded-lg p-3 text-sm text-[#7A5A00]">
                                        <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
                                        <div>
                                            <p className="font-bold">Only part of the regular gifts qualifies.</p>
                                            <p className="text-xs mt-1 text-[#7A5A00]">{fmtGBP(totalExempt)} of {fmtGBP(totalRegular)} falls within surplus income across the years entered. The remainder is treated as potentially chargeable (subject to the annual exemption and taper).</p>
                                        </div>
                                    </div>
                                )}

                                {singleYear && (
                                    <p className="text-[11px] text-ink3 leading-relaxed">
                                        These gifts fall within a single tax year. The s21 exemption requires a <strong>regular pattern</strong> of giving — be prepared to show the pattern was intended and established (e.g. a letter of intent or several years of similar gifts).
                                    </p>
                                )}

                                {/* Evidence checklist */}
                                <div className="bg-paper border border-rule rounded-lg p-3">
                                    <h5 className="text-xs font-bold text-ink2 uppercase mb-2">Evidence executors may need</h5>
                                    <ul className="space-y-1.5 text-xs text-ink3">
                                        {[
                                            'Bank statements showing regular gift payments',
                                            'Pension statements confirming annual income',
                                            'Dividend or interest records (investment income)',
                                            'Evidence of normal household spending patterns',
                                            'Tax returns or P60s (if available)',
                                        ].map((item, i) => (
                                            <li key={i} className="flex items-start gap-2">
                                                <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0 mt-1.5" />
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* Import review modal */}
            {importRows && (
                <GiftImportModal
                    rows={importRows}
                    onConfirm={handleImportConfirm}
                    onClose={() => setImportRows(null)}
                />
            )}
        </div>
    );
};
